import { createEntityAdapter } from "@reduxjs/toolkit";
import { apiSlice } from "../services/api/apiSlice";
import * as endPoint from "../services/api";

// Query params for a master list searched by text, optionally scoped to one institution (instituteSlug)
const masterSearchParams = (arg) => {
  const { search = "", instituteSlug } = arg && typeof arg === "object" ? arg : { search: arg };
  return { search: search ?? "", ...(instituteSlug ? { instituteSlug } : {}) };
};

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getInstitutions: builder.query({
      query: (arg = "") => {
        const { search = "", prm } = arg && typeof arg === "object" ? arg : { search: arg };
        return {
          baseUrl: process.env.REACT_APP_API_MASTER_URL,
          url: endPoint.getInstitutions(),
          method: "GET",
          params: { search: search ?? "", ...(prm ? { prm } : {}) },
        };
      },
      providesTags: ["Institutions"],
    }),
    getResearchAreas: builder.query({
      query: (search = "") => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getResearchAreas(),
        method: "GET",
        params: { search: search ?? "" },
      }),
      providesTags: ["ResearchAreas"],
    }),
    // arg: the search text, or { search, instituteSlug } for courses / departments of that institution only
    // (the server returns the usual master list when the institution isn't on CampusMesh)
    getDegree: builder.query({
      query: (arg = "") => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getDegree(),
        method: "GET",
        params: masterSearchParams(arg),
      }),
      providesTags: ["Degrees"],
    }),
    getFieldOfStudy: builder.query({
      query: (arg = "") => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getFieldOfStudy(),
        method: "GET",
        params: masterSearchParams(arg),
      }),
      providesTags: ["Studyfields"],
    }),
    getAcademicTitle: builder.query({
      query: (search = "") => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getAcademicTitle(),
        method: "GET",
        params: { search: search ?? "" },
      }),
      providesTags: ["academictitle"],
    }),
    getWorkPosition: builder.query({
      query: (search = "") => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getWorkPosition(),
        method: "GET",
        params: { search: search ?? "" },
      }),
      providesTags: ["workPosition"],
    }),
    getLevel: builder.query({
      query: (search = "") => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getLevel(),
        method: "GET",
        params: { search: search ?? "" },
      }),
      providesTags: ["studentlevel"],
    }),
    getCompany: builder.query({
      query: (search = "") => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getCompany(),
        method: "GET",
        params: { search: search ?? "" },
      }),
      providesTags: ["studentlevel"],
    }),
    getEmpType: builder.query({
      query: (search = "") => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getEmpType(),
        method: "GET",
        params: { search: search ?? "" },
      }),
      providesTags: ["studentlevel"],
    }),
    getLocation: builder.query({
      query: (search = "") => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getLocation(),
        method: "GET",
        params: { search: search ?? "" },
      }),
      providesTags: ["studentlevel"],
    }),
    getProjectRole: builder.query({
      query: (search = "") => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getProjectRole(),
        method: "GET",
        params: { search: search ?? "" },
      }),
      providesTags: ["projectRole"],
    }),
    getCertification: builder.query({
      query: (search = "") => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getCertification(),
        method: "GET",
        params: { search: search ?? "" },
      }),
      providesTags: ["certification"],
    }),
    getSkills: builder.query({
      query: (search = "") => {
        return {
          baseUrl: process.env.REACT_APP_API_MASTER_URL,
          url: endPoint.getSkills(),
          method: "GET",
          params: { search: search ?? "" },
        };
      },
      providesTags: ["skills"],
      async onQueryStarted(search, { queryFulfilled }) {
        // try {
        //   const result = await queryFulfilled;
        // } catch (err) {
        //   console.error("Query error:", err);
        // }
      },
    }),

    globalSearch: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_API_URL,
        url: endPoint.globalSearch(),
        method: "GET",
        params: data,
      }),
      providesTags: ["globalSearch"],
    }),
    headerSearch: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_API_URL,
        url: endPoint.headerSearch(),
        method: "GET",
        params: data,
      }),
      providesTags: ["globalSearch"],
    }),
    getAllReactions: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getAllReactions(),
        method: "GET",
        params: data,
      }),
      providesTags: ["postReactions"],
    }),
    getMarketPlaceCategories: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getMarketPlaceCategories(),
        method: "GET",
        params: data,
      }),
      // providesTags: ["postReactions"],
    }),
    getMarketPlaceConditions: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getMarketPlaceConditions(),
        method: "GET",
        params: data,
      }),
      // providesTags: ["postReactions"],
    }),
    getMarketPlacePaymentMethods: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: endPoint.getMarketPlacePaymentMethods(),
        method: "GET",
        params: data,
      }),
      // providesTags: ["paymentMethods"],
    }),
    getCommunityCategories: builder.query({
      query: ({ search = "" }) => ({
        baseUrl: process.env.REACT_APP_API_MASTER_URL,
        url: `${process.env.REACT_APP_STAGE}/master/community-categories`,
        method: "GET",
        params: { search },
      }),
      providesTags: ["communityCategories"],
    }),
    getGeoLocation: builder.query({
      query: (search = "") => ({
        baseUrl: process.env.REACT_APP_API_LOCATION,
        url: endPoint.getGeoLocation(),
        method: "GET",
        params: { text: search ?? "" },
      }),
      // providesTags: ["studentlevel"],
    }),

    getShareList: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_SHARE_URL,
        url: endPoint.getShareList(),
        method: "GET",
      }),
      // providesTags: ["studentlevel"],
    }),

    globalCount: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_GLOBAL_COUNT,
        url: endPoint.getGlobalCount(),
        method: "GET",
      }),
      // providesTags: ["studentlevel"],
    }),

    privacyPolicyList: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_API_URL,
        url: endPoint.getPrivacyPolicyList(),
        method: "GET",
      }),
      // providesTags: ["studentlevel"],
    }),
    privacyPolicyCreate: builder.mutation({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_API_URL,
        url: endPoint.createPrivacyPolicy(), // replace with actual policy-create endpoint
        method: "POST",
        body: data,
      }),
    }),

    getLeaderBoardList: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_CONTEST_API_URL,
        url: endPoint.getLeaderBoardList(),
        method: "GET",
      }),
      // providesTags: ["studentlevel"],
    }),




    getReferCode: builder.mutation({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_CONTEST_API_URL,
        url: endPoint.getReferCode(),
        method: "POST",
        body: data,
      }),
    }),

    getBadges: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_CONTEST_API_URL,
        url: endPoint.getBadges(),
        method: "GET",
      }),
      // providesTags: ["studentlevel"],
    }),

    getTrackCode: builder.query({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_CONTEST_API_URL,
        url: endPoint.getTrackCode(),
        method: "POST",
      }),
      // providesTags: ["studentlevel"],
    }),



    sendUserLocation: builder.mutation({
      query: ({ lat, lon }) => {
        return {
          baseUrl: process.env.REACT_APP_LOCATION_URL, // same pattern as yours

          url: endPoint.sendUserLocation(),
          method: "POST",
          body: {
            lat,
            lon,
          },
        };
      },
    }),


    getSearchUserLocation: builder.query({
      query: (search = "") => ({
        baseUrl: process.env.REACT_APP_LOCATION_URL,
        url: endPoint.searchUserLocation(),
        method: "GET",
        params: { text: search ?? "" },
      }),
      providesTags: ["studentlevel"],
    }),


getMapStyle: builder.query({
      query: () => ({
        baseUrl: process.env.REACT_APP_LOCATION_URL,
        url: endPoint.getMapStyleApi(),
        method: "GET",
      }),
      providesTags: ["mapStyle"],
      transformResponse: (response) => {
        let styleJson;

        // 1. Convert Buffer or deep clone the JSON safely
        if (response?.data?.type === "Buffer") {
          const text = new TextDecoder().decode(new Uint8Array(response.data.data));
          styleJson = JSON.parse(text);
        } else {
          styleJson = JSON.parse(JSON.stringify(response)); 
        }

        // 2. Rewrite AWS tile URLs to our custom protocol
        if (styleJson && styleJson.sources) {
          Object.keys(styleJson.sources).forEach((sourceKey) => {
            const source = styleJson.sources[sourceKey];
            if (source.tiles && Array.isArray(source.tiles)) {
              source.tiles = source.tiles.map((url) => {
                if (url.includes("amazonaws.com")) {
                  return "rtk-tile://{z}/{x}/{y}"; 
                }
                return url;
              });
            }
          });
        }

        // 3. BRING BACK THE STREET NAMES! 
        if (styleJson) {
          // Swap AWS fonts/icons for open-source ones so it doesn't 403 or hang
          if (styleJson.sprite && styleJson.sprite.includes("amazonaws.com")) {
            styleJson.sprite = "https://demotiles.maplibre.org/styles/osm-bright-gl-style/sprite";
          }
          if (styleJson.glyphs && styleJson.glyphs.includes("amazonaws.com")) {
            // Using highly reliable OpenMapTiles fonts
            styleJson.glyphs = "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf";
          }

          // We STILL need to remove layers that require pattern images (like textured parks) 
          // because we changed the sprite sheet, but we KEEP the 'symbol' (text) layers!
          if (styleJson.layers) {
            styleJson.layers = styleJson.layers.filter(layer => {
              // 🚨 NOTICE: We removed the `if (layer.type === 'symbol') return false;` line!
              // This allows MapLibre to draw the street names again.
              
              if (layer.paint && layer.paint['fill-pattern']) return false;
              if (layer.paint && layer.paint['line-pattern']) return false;
              
              return true; 
            });
          }
        }

        return styleJson;
      },
    }),

    // Map tile endpoint
    getMapTile: builder.query({
      query: ({ z, x, y }) => ({
        baseUrl: process.env.REACT_APP_LOCATION_URL,
        url: endPoint.getMapTileApi(z, x, y),  // e.g. (z,x,y) => `/local/location/map/tile/${z}/${x}/${y}`
        method: "GET",
      }),
      providesTags: ["mapTile"],
    }),

  getDragMarkerLocation: builder.query({
  query: ({ lat, lon }) => ({           // ← accept params
    baseUrl: process.env.REACT_APP_LOCATION_URL,
    url: `${endPoint.dragUserLocation()}lat=${lat}&long=${lon}`,  // ← append as query params
    method: "GET",
  }),
  providesTags: ["studentlevel"],
}),

    // Onboarding / Product Tour
    getOnboardingConfigs: builder.query({
      query: () => ({
        baseUrl: process.env.REACT_APP_USER_PREFERENCES_URL,
        url: endPoint.getOnboardingConfigsList(),
        method: "GET",
      }),
      providesTags: ["onboardingConfigs"],
    }),

    updateOnboardingConfig: builder.mutation({
      query: (data) => ({
        baseUrl: process.env.REACT_APP_USER_PREFERENCES_URL,
        url: endPoint.updateOnboardingConfig(),
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["onboardingConfigs"],
    }),

  }),
});

export const {
  useGetInstitutionsQuery,
  useGetResearchAreasQuery,
  useGetDegreeQuery,
  useGetFieldOfStudyQuery,
  useGetAcademicTitleQuery,
  useGetLevelQuery,
  useGetCompanyQuery,
  useGetEmpTypeQuery,
  useGetLocationQuery,
  useGetWorkPositionQuery,
  useLazyGetInstitutionsQuery,
  useGetProjectRoleQuery,
  useGetCertificationQuery,
  useGetSkillsQuery,
  useLazyGlobalSearchQuery,
  useLazyHeaderSearchQuery,
  useGetAllReactionsQuery,
  useGetMarketPlaceCategoriesQuery,
  useGetMarketPlaceConditionsQuery,
  useGetMarketPlacePaymentMethodsQuery,
  useGetCommunityCategoriesQuery,
  useLazyGetCommunityCategoriesQuery,
  useLazyGetGeoLocationQuery,
  useGetShareListQuery,
  useGlobalCountQuery,
  usePrivacyPolicyListQuery,
  usePrivacyPolicyCreateMutation,
  useGetLeaderBoardListQuery,
  useGetReferCodeMutation,
  useGetBadgesQuery,
  useGetTrackCodeQuery,
  useSendUserLocationMutation,
  useLazyGetSearchUserLocationQuery,
  useGetMapStyleQuery,
  useLazyGetMapTileQuery,
  useLazyGetDragMarkerLocationQuery,
  useLazyGetOnboardingConfigsQuery,
  useUpdateOnboardingConfigMutation,
} = extendedApiSlice;
